import { useState } from "react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"

import { Icons } from "../icons"
import { MapIcon } from "./map-icon"

interface MapLegendsProps {
  onStatusFilterChange: (statuses: string[]) => void
  selectedStatuses: string[]
}

export function MapLegends({
  onStatusFilterChange,
  selectedStatuses,
}: MapLegendsProps) {
  const [isFilterMode, setIsFilterMode] = useState(false)

  const statuses = [
    "Active",
    "Disconnected",
    "Delinquent",
    "Pending",
    "Before Due",
    "Due Today",
    "Over Due",
  ]

  const toggleStatusSelection = (status: string) => {
    const newStatuses = selectedStatuses.includes(status)
      ? selectedStatuses.filter((s) => s !== status)
      : [...selectedStatuses, status]
    onStatusFilterChange(newStatuses)
  }

  const toggleAllStatuses = () => {
    if (selectedStatuses.length === statuses.length) {
      onStatusFilterChange([])
    } else {
      onStatusFilterChange([...statuses])
    }
  }

  return (
    <Card className="w-full max-w-xs overflow-hidden rounded-lg shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 bg-primary p-3">
        <CardTitle className="text-lg font-semibold text-white">
          Legend
        </CardTitle>
        <div className="flex items-center space-x-2">
          {isFilterMode && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-white hover:bg-primary/80"
              onClick={toggleAllStatuses}
            >
              {selectedStatuses.length === statuses.length
                ? "Deselect all"
                : "Select all"}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:bg-primary/80"
            onClick={() => setIsFilterMode(!isFilterMode)}
          >
            <Icons.listFilter className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-2 p-3">
        <div className="grid grid-cols-2 gap-2">
          {statuses.map((status) => (
            <div
              key={status}
              className={`flex items-center gap-2 rounded-md p-2 transition-colors ${
                isFilterMode && selectedStatuses.includes(status)
                  ? "bg-primary/10"
                  : "hover:bg-muted"
              }`}
              onClick={() => isFilterMode && toggleStatusSelection(status)}
            >
              {isFilterMode ? (
                <Checkbox
                  id={status}
                  checked={selectedStatuses.includes(status)}
                  className="h-4 w-4"
                  onCheckedChange={() => toggleStatusSelection(status)}
                />
              ) : (
                <MapIcon connectionStatus={status} />
              )}
              <label
                htmlFor={status}
                className="cursor-pointer text-sm font-medium leading-none"
              >
                {status}
              </label>
            </div>
          ))}
        </div>

        {isFilterMode && (
          <div className="border-t pt-2">
            <Button
              variant="default"
              size="sm"
              className="w-full"
              onClick={() => setIsFilterMode(false)}
            >
              Apply Filters ({selectedStatuses.length})
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
